import { t } from 'i18next';
import moment from 'moment';
import { DetailedHTMLProps, AnchorHTMLAttributes,Component, ReactElement, ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { TShortStorageItemSummary } from '../../../../../../../shared/types/storageTypes';
import { Media } from '../../../../../../../shared/frontend/components/presentational/media';
import { Spinner } from '../../../../../../../shared/frontend/components/presentational/spinner';
import { naturalShortDateNoYear, plural, relativeDate } from '../../../../../../../shared/frontend/libs/utils';
import { TError } from '../../../../../../../shared/frontend/scripts/actions/errorActions';
import { findSeriesLink } from '../../../../../../../shared/frontend/scripts/article/utils';
import { asset, scaledAssetUrl } from '../../../../../../../shared/frontend/services/assetResolverService';
import { ArticleParticipantCategory } from '../../../../../shared/enums';
import { RouteComponentProps } from '../../../../../../../shared/common/types/common';
import { Link } from '../../../../../../../shared/frontend/components/link';

/* tslint:disable:max-line-length */

type TPropsFromState = {
  details?: any;
  isLoading?: boolean;
  isLoaded?: boolean;
  showMythicPlusShare?: boolean;
  setPageTitle?: boolean;
};

type TPropsThatDispatch = {
  setNewsListContent(params: any): void;
  setNewsListLoading(loading: boolean): void;
  showError(actionData: TError): void;
};

type TOwnProps = {
  tag: string[];
  user: string;
  p: number;
};

export interface IArticle {
  title: string;
  image_url: string;
  published_at: string;
  description: string;
  redirect_url: string;
  is_stickied: boolean;
  path: string;
  tags: string[];
  participants: any[];
  author: {
    name: string;
  };
  hearts?: number;
  url?: string;
  image?: TShortStorageItemSummary | null;
}

export type TKeystoneRunContentProps = TOwnProps &
  TPropsFromState &
  Partial<TPropsThatDispatch> &
  RouteComponentProps<any, any>;

type TNewsListEntryProps = {
  limit: number;
  maxPerRow: number;
  article: IArticle;
  showAuthor?: boolean;
};

const MAX_DAYS_BEFORE_HIDING_DATE = 14;

function NewsListEntryLarge(props: TNewsListEntryProps) {
  const { article, showAuthor = true } = props;

  const coAuthors = [];

  if (article.participants?.length) {
    for (const author of article.participants) {
      if (author.user && author.category === ArticleParticipantCategory.CO_AUTHOR) {
        coAuthors.push(author.user.name);
      }
    }
  }

  const NEWS_ASSET_SIZE = {
    quality: 75,
    width: 400
  };

  return (
    <div
      className={`slds-size--1-of-${props.maxPerRow} slds-max-small-size--1-of-1 slds-max-medium-size--1-of-2 slds-p-horizontal--xx-small slds-shrink-none slds-p-bottom--small`}
      key={article.redirect_url || article.path || article.url}
    >
      <ArticleLink
        article={article}
        className="rio-news-element--imagelink"
        style={{
          backgroundImage: `url(${
            scaledAssetUrl(article.image?.url, NEWS_ASSET_SIZE) ||
            asset(article.image_url || '/images/news/headlines/default_listing_image.jpg', NEWS_ASSET_SIZE)
          }), linear-gradient(rgba(0, 0, 0, 0.91), rgba(0, 0, 0, 0.91)), url(${
            scaledAssetUrl(article.image?.url, NEWS_ASSET_SIZE) ||
            asset(article.image_url || '/images/news/headlines/default_listing_image.jpg', NEWS_ASSET_SIZE)
          })`
        }}
      >
        {article.is_stickied && (
          <i className="fa fa-thumbtack fa-rotate-by text-white rio-text-shadow--normal rio-news-element--pin" />
        )}
      </ArticleLink>
      <div>
        <ArticleLink article={article} className="rio-realm-link slds-text-heading--small">
          {article.title}
        </ArticleLink>
      </div>
      <div className="slds-grid slds-grid--align-spread">
        <div className="slds-col slds-text-align--left">
          <span
            className="rio-frontpage-article--byline text-muted rio-takeover-hidden slds-m-right--small"
            title={naturalShortDateNoYear(article.published_at)}
          >
            {moment.duration(moment.utc().diff(moment.utc(article.published_at))).asDays() < MAX_DAYS_BEFORE_HIDING_DATE
              ? `Posted ${relativeDate(article.published_at)}`
              : 'Posted'}{' '}
            {showAuthor && (
              <>
                by{' '}
                <span>
                  <Link className="rio-realm-link text-muted slds-wrap" to={`/news?user=${article.author.name}`}>
                    {article.author.name}
                  </Link>
                  {coAuthors.length === 1 && (
                    <Trans
                      i18nKey="news:co-author"
                      values={{
                        coAuthor: coAuthors[0]
                      }}
                    >
                      <Link className="rio-realm-link text-muted slds-wrap" to={`/news?user=${coAuthors[0]}`} />
                    </Trans>
                  )}
                  {coAuthors.length > 1 && t('news:co-author-count-more', { count: coAuthors.length })}
                </span>
              </>
            )}
          </span>
        </div>
        {article.hearts && article.hearts > 0 && (
          <div className="slds-col slds-text-align--right">
            <span className="text-white" title={`${article.hearts} ${plural('Heart', article.hearts)}`}>
              {article.hearts} <i className="fa fa-heart" />
            </span>
          </div>
        )}
      </div>

      {/* {article.tags.length > 0 && (
        <div className="slds-m-top--x-small">
          <div className="slds-p-left--x-small rio-frontpage-news--tags">
            {article.tags.map((tag: any) => (
              <Link
                key={tag}
                to={`/news?tag=${tag}`}
                className="slds-m-right--small slds-text-size--small rio-realm-link text-muted"
              >
                {tag.toUpperCase().replace(/-/g, ' ')}
              </Link>
            ))}
          </div>
        </div>
      )} */}
    </div>
  );
}

function NewsListEntrySmall(props: TNewsListEntryProps) {
  const { article } = props;

  const linkUrl = findSeriesLink(article);

  return (
    <div
      className={'slds-size--1-of-1 slds-p-horizontal--x-small slds-shrink-none slds-p-bottom--x-small'}
      key={linkUrl}
    >
      <div className="slds-grid">
        <div className="slds-col slds-grow-none" style={{ borderRight: '3px solid #ffbd00' }}>
          &nbsp;
        </div>
        <div className="slds-col slds-p-left--x-small">
          <Link to={linkUrl} className="rio-realm-link xslds-text-title--bold">
            {article.title}
          </Link>
        </div>
      </div>
    </div>
  );
}

interface INewsListContentProps {
  isLoaded: boolean;
  articles: IArticle[];
  limit: number;
  maxPerRow: number;
  mobileLimit: number;
  showAuthor?: boolean;
  centered?: boolean;
}

export default class NewsListContent extends Component<INewsListContentProps, any> {
  public render(): ReactElement<any> {
    if (!this.props.isLoaded) {
      return (
        <div style={{ position: 'relative' }} className="slds-text-align--center">
          <Spinner />
        </div>
      );
    }

    const maxPerRow = this.props.maxPerRow || this.props.limit;
    const emptySlate =
      this.props.articles.length === 0 ? (
        <div className="slds-m-around--x-large">
          <h1 className="slds-text-heading--large">{t('news:no-articles')}</h1>
        </div>
      ) : null;

    const { centered = true } = this.props;

    return (
      <div className="rio-news-list">
        <Media at="sm">
          <div className="slds-grid slds-wrap slds-grid--align-center">
            {emptySlate}
            {this.props.articles.slice(0, this.props.mobileLimit || this.props.articles.length).map((article: any) => (
              <NewsListEntrySmall article={article} limit={this.props.limit} maxPerRow={maxPerRow} key={article.path} />
            ))}
          </div>
        </Media>

        <Media greaterThan="sm">
          <div className={`slds-grid slds-wrap ${centered && 'slds-grid--align-center'}`}>
            {emptySlate}
            {this.props.articles.map((article: any) => (
              <NewsListEntryLarge
                showAuthor={this.props.showAuthor}
                article={article}
                limit={this.props.limit}
                maxPerRow={maxPerRow}
                key={article.path}
              />
            ))}
          </div>
        </Media>
      </div>
    );
  }
}

interface IArticleLinkProps
  extends DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement> {
  article: IArticle;
  children: ReactNode;
}

const ArticleLink = ({ article, children, ...restProps }: IArticleLinkProps) => {
  if (article.redirect_url) {
    return (
      <a {...restProps} href={article.redirect_url} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }

  const linkUrl = findSeriesLink(article);

  return (
    <Link {...restProps} to={linkUrl}>
      {children}
    </Link>
  );
};
